import BackButton from "@dashboard/components/BackButton";
import { ConfirmButton, ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import ControlledCheckbox from "@dashboard/components/ControlledCheckbox";
import Form from "@dashboard/components/Form";
import { DashboardModal } from "@dashboard/components/Modal";
import { OrderErrorFragment } from "@dashboard/graphql";
import useModalDialogErrors from "@dashboard/hooks/useModalDialogErrors";
import getOrderErrorMessage from "@dashboard/utils/errors/order";
import { TextField } from "@material-ui/core";
import { Box, Option, Select, Text } from "@saleor/macaw-ui-next";
import { FormattedMessage, useIntl } from "react-intl";

import { cancelOrderDialogMessages } from "./messages";

const OTHER_REASON = "other";

export interface OrderCancelFormData {
  // Human-readable cancellation reason resolved from the dropdown (or freehand text).
  reason: string;
  // Whether the cancellation email is sent to the customer at all.
  notifyCustomer: boolean;
  // Whether the reason should be shared with the customer (email/metadata) or kept internal only.
  notifyReason: boolean;
}

interface FormData {
  reason: string;
  reasonNote: string;
  notifyCustomer: boolean;
  notifyReason: boolean;
}

interface OrderCancelDialogProps {
  confirmButtonState: ConfirmButtonTransitionState;
  errors: OrderErrorFragment[];
  number: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: OrderCancelFormData) => void;
}

export const OrderCancelDialog = (props: OrderCancelDialogProps) => {
  const {
    confirmButtonState,
    errors: apiErrors,
    number: orderNumber,
    open,
    onSubmit,
    onClose,
  } = props;
  const intl = useIntl();
  const errors = useModalDialogErrors(apiErrors, open);

  const reasonOptions: Option[] = [
    {
      value: "missing-payment",
      label: intl.formatMessage(cancelOrderDialogMessages.reasonMissingPayment),
    },
    {
      value: "out-of-stock",
      label: intl.formatMessage(cancelOrderDialogMessages.reasonOutOfStock),
    },
    {
      value: "customer-request",
      label: intl.formatMessage(cancelOrderDialogMessages.reasonCustomerRequest),
    },
    { value: "duplicate", label: intl.formatMessage(cancelOrderDialogMessages.reasonDuplicate) },
    { value: "fraud", label: intl.formatMessage(cancelOrderDialogMessages.reasonFraud) },
    { value: OTHER_REASON, label: intl.formatMessage(cancelOrderDialogMessages.reasonOther) },
  ];

  const initial: FormData = {
    reason: "",
    reasonNote: "",
    notifyCustomer: true,
    notifyReason: true,
  };

  const resolveReason = (data: FormData): string => {
    if (data.reason === OTHER_REASON) {
      return data.reasonNote.trim();
    }

    return reasonOptions.find(option => option.value === data.reason)?.label ?? "";
  };

  const handleSubmit = (data: FormData) =>
    onSubmit({
      reason: resolveReason(data),
      notifyCustomer: data.notifyCustomer,
      notifyReason: data.notifyCustomer && data.notifyReason,
    });

  const isValid = (data: FormData): boolean =>
    data.reason !== "" && (data.reason !== OTHER_REASON || data.reasonNote.trim() !== "");

  return (
    <DashboardModal onChange={onClose} open={open}>
      <DashboardModal.Content size="sm">
        <DashboardModal.Header data-test-id="dialog-title">
          <FormattedMessage {...cancelOrderDialogMessages.dialogTitle} values={{ orderNumber }} />
        </DashboardModal.Header>
        <Form initial={initial} onSubmit={handleSubmit}>
          {({ change, data, submit }) => (
            <Box display="grid" gap={4}>
              <Text>
                <FormattedMessage
                  {...cancelOrderDialogMessages.dialogContent}
                  values={{
                    b: (...chunks) => <b>{chunks}</b>,
                  }}
                />
              </Text>

              <Select
                options={reasonOptions}
                label={intl.formatMessage(cancelOrderDialogMessages.reasonLabel)}
                name="reason"
                data-test-id="cancel-reason-select"
                value={data.reason}
                onChange={value => change({ target: { name: "reason", value } })}
              />

              {data.reason === OTHER_REASON && (
                <TextField
                  fullWidth
                  name="reasonNote"
                  label={intl.formatMessage(cancelOrderDialogMessages.reasonOtherLabel)}
                  value={data.reasonNote}
                  onChange={change}
                  data-test-id="cancel-reason-note"
                />
              )}

              <ControlledCheckbox
                data-test-id="cancel-notify-customer"
                name="notifyCustomer"
                label={intl.formatMessage(cancelOrderDialogMessages.notifyCustomerLabel)}
                checked={data.notifyCustomer}
                onChange={change}
              />

              {data.notifyCustomer && (
                <ControlledCheckbox
                  data-test-id="cancel-notify-reason"
                  name="notifyReason"
                  label={intl.formatMessage(cancelOrderDialogMessages.notifyReasonLabel)}
                  checked={data.notifyReason}
                  onChange={change}
                />
              )}

              {errors.length > 0 &&
                errors.map((err, index) => (
                  <Text display="block" color="critical1" key={index} data-test-id="dialog-error">
                    {getOrderErrorMessage(err, intl)}
                  </Text>
                ))}

              <DashboardModal.Actions>
                <BackButton onClick={onClose}>
                  <FormattedMessage {...cancelOrderDialogMessages.buttonKeepOrder} />
                </BackButton>
                <ConfirmButton
                  onClick={submit}
                  transitionState={confirmButtonState}
                  disabled={!isValid(data)}
                  data-test-id="submit"
                >
                  <FormattedMessage {...cancelOrderDialogMessages.buttonCancelOrder} />
                </ConfirmButton>
              </DashboardModal.Actions>
            </Box>
          )}
        </Form>
      </DashboardModal.Content>
    </DashboardModal>
  );
};
OrderCancelDialog.displayName = "OrderCancelDialog";
export default OrderCancelDialog;
